import { forwardRef, type HTMLAttributes } from 'react'

type Gap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline'
type Justify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'

interface FlexProps extends HTMLAttributes<HTMLDivElement> {
  gap?: Gap
  align?: Align
  justify?: Justify
  wrap?: boolean
  inline?: boolean
  as?: 'div' | 'section' | 'article' | 'main' | 'aside' | 'nav' | 'header' | 'footer'
}

const gapClasses: Record<Gap, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
}

const alignClasses: Record<Align, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
}

const justifyClasses: Record<Justify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
}

/**
 * Flex - Horizontal flex container with consistent gap spacing
 *
 * @example
 * <Flex gap={2} align="center" justify="between">
 *   <Text>Left</Text>
 *   <Text>Right</Text>
 * </Flex>
 *
 * @example
 * <Flex gap={4} wrap>
 *   {items.map(item => <Card key={item.id} />)}
 * </Flex>
 */
const Flex = forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      gap = 2,
      align = 'center',
      justify = 'start',
      wrap = false,
      inline = false,
      as: Component = 'div',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const displayClass = inline ? 'inline-flex' : 'flex'
    const wrapClass = wrap ? 'flex-wrap' : ''

    return (
      <Component
        ref={ref}
        className={`${displayClass} ${gapClasses[gap]} ${alignClasses[align]} ${justifyClasses[justify]} ${wrapClass} ${className}`}
        {...props}
      >
        {children}
      </Component>
    )
  }
)

Flex.displayName = 'Flex'

export default Flex
